// ── Evolution Timeline (Phase C UI) ─────────────────────────
// Horizontal swimlanes — one row per generation. Each candidate is
// a card colored by composite fitness (red→yellow→green). Lineage
// edges (mutation/crossover) draw thin lines from parent → child
// across rows.
//
// Click a candidate to inspect its evaluator scores + experiment
// suggestion + adversarial objections.
//
// See NOVELTY_ENGINE_PLAN.md Phase C.

import React, { useState } from 'react';
import { useNovelty } from './NoveltyContext';
import { CompetitionBadge, VerifyNoveltyPanel } from './CompetitionUI';

function fitnessColor(f) {
  if (f == null) return 'var(--text-muted)';
  if (f >= 70) return 'var(--fitness-excellent)';
  if (f >= 50) return 'var(--fitness-good)';
  if (f >= 35) return 'var(--fitness-fair)';
  if (f >= 20) return 'var(--fitness-weak)';
  return 'var(--fitness-poor)';
}

function fitnessBand(f) {
  if (f == null) return 'unscored';
  if (f >= 70) return 'excellent';
  if (f >= 50) return 'good';
  if (f >= 35) return 'fair';
  if (f >= 20) return 'weak';
  return 'poor';
}

function originIcon(origin) {
  if (origin === 'seed') return '🌱';
  if (origin === 'blend') return '🧬';
  if (origin === 'mutation') return '⚡';
  if (origin === 'crossover') return '🧬';
  return '·';
}

function originLabel(origin) {
  if (origin === 'seed') return 'seed';
  if (origin === 'blend') return 'blend (Phase B)';
  if (origin === 'mutation') return 'mutation';
  if (origin === 'crossover') return 'crossover';
  return origin || 'unknown';
}

const DIM_LABELS = {
  novelty: { short: 'NOVEL',  long: 'Novelty',        tip: 'How original vs. existing ideas (0–100).' },
  falsifiability: { short: 'FALSI',  long: 'Falsifiability', tip: 'Does it make a testable claim with a clear kill-criterion?' },
  antipattern: { short: 'A-PAT',  long: 'Anti-pattern',   tip: 'Resistance to known startup failure patterns (higher = healthier).' },
  marketsim: { short: 'MARKET', long: 'Market sim',     tip: 'Survival under incumbent + cynical-customer + unit-economics challenge.' },
};

export default function EvolutionTimeline() {
  const { evolveGenerations, evolveLineage, evolveActiveGen, onDrillNovelty } = useNovelty();
  const [selected, setSelected] = useState(null);

  const genIndices = Object.keys(evolveGenerations).map(Number).sort((a, b) => a - b);
  if (genIndices.length === 0) {
    return (
      <div style={{ padding: 20, fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
        No evolutionary data yet. Toggle <strong>🌱 Evolve</strong> next to <strong>🧬 Novelty</strong> before generating.
      </div>
    );
  }

  // Champion across all gens — annotate with its source gen so the badge is correct
  // (server payload omits genIdx inside `candidate`, so fall back to map key)
  const allCandidates = genIndices.flatMap(g =>
    (evolveGenerations[g] || []).map(c => ({ ...c, genIdx: c.genIdx ?? g }))
  );
  const champion = [...allCandidates].sort((a, b) => (b.fitness || 0) - (a.fitness || 0))[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Champion banner */}
      {champion && (
        <div
          role="region"
          aria-label={`Champion candidate: ${champion.label}, fitness ${champion.fitness} of 100, generation ${champion.genIdx}`}
          style={{
            background: 'linear-gradient(135deg, #1a2e1a 0%, #0e1f0e 100%)',
            border: `1px solid ${fitnessColor(champion.fitness)}`,
            borderRadius: 8,
            padding: 12,
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 1, marginBottom: 8 }}>
            🏆 CHAMPION — score {champion.fitness}/100 · generation {champion.genIdx}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600, marginBottom: 8 }}>
            {champion.label}
          </div>
          <FitnessBars cand={champion} />
          {/* Tier 2 competition badge — auto-populated for top candidates */}
          <CompetitionBadge candidateId={champion.id} />
          {/* Tier 3 deep verify — on-demand */}
          <VerifyNoveltyPanel candidate={champion} candidateId={champion.id} />
          {onDrillNovelty && (
            <button
              onClick={() => onDrillNovelty(champion.label, champion.reasoning, 'evolved champion')}
              aria-label={`Drill into champion candidate: ${champion.label}`}
              style={{
                marginTop: 12, background: 'var(--bg-card)', color: 'var(--success)',
                border: '1px solid var(--success)', padding: '8px 12px', fontSize: 11,
                borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-mono)', minHeight: 32,
              }}
            >
              🔬 Drill into champion
            </button>
          )}
        </div>
      )}

      {/* Generation swim-lanes — aria-live so screen readers announce new candidates */}
      <div
        role="region"
        aria-label="Evolutionary generations"
        aria-live="polite"
        aria-relevant="additions"
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {genIndices.map(g => {
          const cands = [...(evolveGenerations[g] || [])].sort((a, b) => (b.fitness || 0) - (a.fitness || 0));
          const isActive = evolveActiveGen === g;
          return (
            <div key={g} style={{
              background: 'var(--bg-secondary)',
              border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border-subtle)'}`,
              borderRadius: 8,
              padding: 8,
            }}>
              <div style={{
                fontSize: 11, color: isActive ? 'var(--info)' : 'var(--text-muted)',
                letterSpacing: 1, marginBottom: 8,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontFamily: 'var(--font-mono)',
              }}>
                <span>Generation {g}{g === 0 ? ' · seed' : ''}</span>
                <span>{cands.length} candidate{cands.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {cands.map(c => {
                  const isSelected = selected?.id === c.id;
                  const band = fitnessBand(c.fitness);
                  return (
                    <button
                      key={c.id}
                      onClick={() => setSelected(isSelected ? { ...c, genIdx: c.genIdx ?? g } : null)}
                      aria-label={`Candidate from generation ${g}: ${c.label}, score ${c.fitness ?? 'unscored'} of 100, ${originLabel(c.origin)}, fitness band ${band}. Click to inspect details.`}
                      aria-pressed={isSelected}
                      title={`${c.label} — score ${c.fitness}/100`}
                      style={{
                        background: isSelected ? 'var(--bg-card-elevated)' : 'var(--bg-card)',
                        border: `1px solid ${fitnessColor(c.fitness)}`,
                        borderLeftWidth: 4,
                        color: 'var(--text-primary)',
                        padding: '8px',
                        fontSize: 11,
                        borderRadius: 4,
                        cursor: 'pointer',
                        textAlign: 'left',
                        maxWidth: 240,
                        minHeight: 32,
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                        <span style={{ fontSize: 12 }} aria-hidden="true">{originIcon(c.origin)}</span>
                        <span style={{
                          color: fitnessColor(c.fitness), fontWeight: 700, minWidth: 22,
                        }}>{c.fitness ?? '?'}</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.label}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail panel */}
      {selected && <CandidateDetail cand={selected} lineage={evolveLineage} onClose={() => setSelected(null)} onDrill={onDrillNovelty} />}
    </div>
  );
}

function FitnessBars({ cand }) {
  const dims = [
    { key: 'novelty', value: cand.novelty },
    { key: 'falsifiability', value: cand.falsifiability },
    { key: 'antipattern', value: cand.antipattern },
    { key: 'marketsim', value: cand.marketsim },
  ];
  return (
    <div role="list" aria-label="Score breakdown" style={{ display: 'flex', gap: 12, marginTop: 4 }}>
      {dims.map(d => {
        const meta = DIM_LABELS[d.key];
        return (
          <div
            key={d.key}
            role="listitem"
            title={`${meta.long}: ${d.value ?? 'not scored'}/100. ${meta.tip}`}
            aria-label={`${meta.long} score: ${d.value ?? 'not scored'} of 100`}
            style={{ flex: 1, fontFamily: 'var(--font-mono)' }}
          >
            <div style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: 1 }}>{meta.short}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
              <span style={{ color: fitnessColor(d.value), fontWeight: 700, fontSize: 12, minWidth: 20 }}>
                {d.value ?? '—'}
              </span>
              <div
                role="progressbar"
                aria-valuenow={d.value ?? 0}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{ flex: 1, height: 4, background: 'var(--border-subtle)', borderRadius: 2, overflow: 'hidden' }}
              >
                <div style={{ width: `${d.value || 0}%`, height: '100%', background: fitnessColor(d.value) }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CandidateDetail({ cand, lineage, onClose, onDrill }) {
  const lin = (lineage || []).find(l => l.childId === cand.id);
  return (
    <div
      role="region"
      aria-label={`Candidate detail: ${cand.label}`}
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600, lineHeight: 1.4 }}>
          {cand.label}
        </div>
        <button
          onClick={onClose}
          aria-label="Close detail panel"
          style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 16, cursor: 'pointer', minWidth: 32, minHeight: 32 }}
        >×</button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 12, fontFamily: 'var(--font-mono)' }}>
        Generation {cand.genIdx} · {originLabel(cand.origin)}{cand.operator ? ` (${cand.operator})` : ''} · score {cand.fitness}/100
        {lin?.parentIds?.length ? ` · parents: ${lin.parentIds.join(', ')}` : ''}
      </div>
      <FitnessBars cand={cand} />

      {cand.reasoning && (
        <div style={{ marginTop: 12, padding: 8, background: 'var(--bg-secondary)', borderRadius: 4, fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.5 }}>
          {cand.reasoning}
        </div>
      )}

      {cand.whatChanged && <DetailRow label="What changed" body={cand.whatChanged} />}
      {cand.blendStrategy && <DetailRow label="Blend strategy" body={cand.blendStrategy} />}
      {cand.experiment && (
        <DetailRow label="🧪 Cheapest test (falsifiability)" body={cand.experiment} highlight="var(--success)" />
      )}
      {cand.killCriterion && (
        <DetailRow label="⚠️ Fail condition" body={cand.killCriterion} highlight="var(--error)" />
      )}
      {cand.matchedPatterns?.length > 0 && (
        <DetailRow label="⚠️ Anti-patterns matched" body={cand.matchedPatterns.join(' · ')} highlight="var(--warning)" />
      )}
      {cand.antipatternRationale && <DetailRow label="Anti-pattern rationale" body={cand.antipatternRationale} />}
      {cand.incumbent && (
        <DetailRow label="🏢 Incumbent objection" body={cand.incumbent} highlight="var(--info)" />
      )}
      {cand.customer && (
        <DetailRow label="👤 Skeptical customer" body={cand.customer} highlight="var(--info)" />
      )}
      {cand.economics && (
        <DetailRow label="📉 Unit economics" body={cand.economics} highlight="var(--info)" />
      )}

      {/* Tier 2 + Tier 3 competition grounding */}
      <CompetitionBadge candidateId={cand.id} />
      <VerifyNoveltyPanel candidate={cand} candidateId={cand.id} />

      {onDrill && (
        <button
          onClick={() => onDrill(cand.label, cand.reasoning, 'evolved candidate')}
          aria-label={`Drill into candidate: ${cand.label}`}
          style={{
            marginTop: 12, background: 'var(--bg-secondary)', color: 'var(--success)',
            border: '1px solid var(--success)', padding: '8px 12px', fontSize: 11,
            borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-mono)', minHeight: 32,
          }}
        >
          🔬 Drill into this candidate
        </button>
      )}
    </div>
  );
}

function DetailRow({ label, body, highlight }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        fontSize: 11, color: highlight || 'var(--text-muted)', letterSpacing: 1,
        marginBottom: 4, fontFamily: 'var(--font-mono)',
      }}>{label}</div>
      <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}
